var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Move } from "@itsy/animate/move";
import { i18n } from "@lingui/core";
import { css, html, LitElement, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import "../icon/icon.js";
import { activateI18n, detectLocale } from "../i18n.js";
import { messages as daMessages } from "../modal/locales/da/messages.mjs";
import { messages as enMessages } from "../modal/locales/en/messages.mjs";
import { messages as fiMessages } from "../modal/locales/fi/messages.mjs";
import { messages as nbMessages } from "../modal/locales/nb/messages.mjs";
import { messages as svMessages } from "../modal/locales/sv/messages.mjs";
import { CanCloseMixin } from "../modal/util.js";
import { reset } from "../styles.js";
/**
 * The header section of a modal, typically where you place the title and a close button.
 *
 * @parent w-modal
 *
 * @slot top - Customize the title bar, for example to have a header image that reaches the modal's edges. See the With Image story for an example.
 */
export class WarpModalHeader extends CanCloseMixin(LitElement) {
    constructor() {
        super();
        /**
         * Whether the modal header should have a back button
         */
        this.back = false;
        /**
         * Lets you hide the close button in the header
         */
        this.noClose = false;
        this._hasTopContent = false;
        activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
    }
    render() {
        return html `
			<div class="header">
				<slot name="top" @slotchange=${this.handleTopSlotChange}></slot>
				<div class="${this._hasTopContent ? "" : "header-title-bar"}">
					${this.backButton}
					<h1 class="title-el ${this.titleClasses}">${this.title}</h1>
					${this.closeButton}
				</div>
			</div>
		`;
    }
    async willUpdate(changedProperties) {
        if (changedProperties.has("back")) {
            if (!this.titleEl)
                return;
            const move = new Move(this.titleEl);
            move.when(async () => {
                await this.updateComplete;
            });
        }
    }
    get titleClasses() {
        return [
            "header-title",
            this.back
                ? "header-title-with-back-button"
                : "header-title-without-back-button",
            this._hasTopContent ? "header-title-with-top-area" : "",
        ].join(" ");
    }
    get backButton() {
        return this.back && !this._hasTopContent // Not showing back button when there is a top image
            ? html `<button
					type="button"
					title=""
					aria-label="${i18n._({
                id: "modal.aria.back",
                message: "Back",
                comment: "Aria label for the back button in modal",
            })}"
					class="header-button header-button-left"
					@click="${this.emitBack}"
				>
					<w-icon
						name="ArrowLeft"
						size="small"
						locale="${detectLocale()}"
						style="display: flex;"
						class="flex"
					></w-icon>
				</button>`
            : nothing;
    }
    get closeButton() {
        if (this.noClose)
            return nothing;
        return html `<div class="header-close-button-container">
			<w-button
				type="button"
				title=""
				aria-label="${i18n._({
            id: "modal.aria.close",
            message: "Close",
            comment: "Aria label for the close button in modal",
        })}"
				variant="pill"
				small
				@click="${this.close}"
			>
				<w-icon
					name="Close"
					size="small"
					locale="${detectLocale()}"
					style="display: flex;"
					class="flex"
				></w-icon>
			</w-button>
		</div>`;
    }
    emitBack() {
        this.dispatchEvent(new CustomEvent("backClicked", { bubbles: true, composed: true }));
    }
    handleTopSlotChange(slotEvent) {
        const topContent = slotEvent.target.assignedElements({
            flatten: true,
        });
        this._hasTopContent = !!topContent.length;
    }
    static { this.styles = [
        reset,
        css `
			.header {
				position: relative;
				padding-bottom: 0.8rem;
			}
			.header-title-bar {
				display: grid;
				flex-shrink: 0 !important;
				gap: 1.2rem;
				grid-template-columns: auto 1fr auto;
				align-items: flex-start;
				padding-left: 1.6rem;
				padding-right: 1.6rem;
				padding-top: 1.6rem;
			}
			.header-title {
				font-weight: 700;
				font-size: var(--w-font-size-l);
				line-height: var(--w-line-height-l);
				align-self: center;
				margin: 0;
			}
			.header-title-with-back-button {
				justify-self: center;
			}
			.header-title-without-back-button {
				grid-column: span 2 / span 2;
			}
			.header-title-with-top-area {
				padding-left: 1.6rem;
				padding-right: 1.6rem;
				padding-top: 1.6rem;
			}
			.header-button:hover {
				-webkit-background-clip: padding-box;
				background-clip: padding-box;
			}
			.header-button:focus,
			.header-button:focus-visible {
				outline: 2px solid var(--w-s-color-border-focus);
				outline-offset: var(--w-outline-offset, 1px);
			}
			.header-button:not(:focus-visible) {
				outline: none;
			}
			.header-button {
				border-width: 0;
				border-radius: 9999px;
				display: inline-flex;
				align-items: center;
				justify-content: center;
				min-height: 40px;
				min-width: 40px;
				padding: 0.4rem;
				font-weight: 700;
				transition-property:
					color, background-color, border-color, text-decoration-color, fill,
					stroke;
				transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
				transition-duration: 150ms;
				font-size: var(--w-font-size-m);
				line-height: var(--w-line-height-m);
				line-height: 2.4rem;
			}
			.header-button-left {
				background-color: transparent;
				color: var(--w-s-color-icon);
				margin-left: -0.8rem;
			}
			.header-button-left:hover {
				background-color: var(--w-color-button-pill-background-hover);
			}
			.header-button-left:active {
				background-color: var(--w-color-button-pill-background-active);
			}
			.header-close-button-container {
				position: absolute;
				right: 2rem;
				top: 2.4rem;
			}
			@media (min-width: 480px) {
				.header-title-bar {
					padding-left: 3.2rem;
					padding-right: 3.2rem;
					padding-top: 2.4rem;
				}
				.header-title-with-top-area {
					padding-left: 3.2rem;
					padding-right: 3.2rem;
				}
				.header-button {
					min-height: 32px;
					min-width: 32px;
				}
			}
		`,
    ]; }
}
__decorate([
    property({ type: String })
], WarpModalHeader.prototype, "title", void 0);
__decorate([
    property({ type: Boolean })
], WarpModalHeader.prototype, "back", void 0);
__decorate([
    property({ type: Boolean, attribute: "no-close" })
], WarpModalHeader.prototype, "noClose", void 0);
__decorate([
    state()
], WarpModalHeader.prototype, "_hasTopContent", void 0);
__decorate([
    query(".title-el")
], WarpModalHeader.prototype, "titleEl", void 0);
/** @deprecated Exported for backwards compatibility. Use WarpModalHeader. */
export const ModalHeader = WarpModalHeader;
if (!customElements.get("w-modal-header")) {
    customElements.define("w-modal-header", WarpModalHeader);
}
//# sourceMappingURL=modal-header.js.map